import { useAppSelector } from "@redux/hooks";

/**
 * Tenant id forwarded as `?tenantId=` to backend calls that respect the
 * dashboard's "tenant lens" (`getTenantId(req)` / `resolveTagsScope(req)`
 * on the backend, depending on the endpoint).
 *
 * The hook returns the value to forward; `undefined` means "omit the
 * override and let the backend resolve from the JWT". The exact rule
 * depends on the caller's role because the dashboard's convention and
 * the contract (TAGS-API-CONTRACT.md §3.0) interact differently per role:
 *
 * 1. **Global Admin** — ALWAYS forward the dropdown's `tenantId` when a
 *    selection exists, even in "Partner view" (`tenantId === partnerId`).
 *    Without the override the backend interprets the request as "find
 *    everything in the system" (`findAll` per contract §3.0), which is
 *    NOT what the dashboard wants — the user explicitly picked a partner
 *    in the SideNav dropdown. The dashboard has no UX to express
 *    "browse everything"; every screen is always scoped to a partner
 *    (the dropdown auto-picks the first partner on boot — see
 *    `PartnerCustomerDropdown::fetchPartners`). So forwarding the
 *    override is the only way to honor the visible selection.
 *
 * 2. **Partner** — Omit the override in Partner view
 *    (`tenantId === partnerId === own`). The backend resolves the
 *    tenant from the JWT (which IS the partner) and expands to
 *    `[own, ...directChildren]`, matching the dropdown's "X (Partner
 *    Account)" intent. Forwarding the override is functionally
 *    equivalent but hits a coercion bug in the legacy
 *    `controllers/KontrolEdge/helpers/common.js::getTenantId` for users
 *    that have `Tenant.list` permission — `queryTenantId || userTenantId`
 *    returns the raw STRING (`"10"`), which mismatches the INTEGER
 *    `Tags.tenantId` in Sequelize. Omitting sidesteps that path. For
 *    Customer view (`tenantId !== partnerId`) we forward the override
 *    so the backend scopes to that child.
 *
 * 3. **Customer** — Forward `tenantId` when one is set; the backend
 *    silently ignores any override that's not own (per contract §3.0),
 *    so this is harmless and matches Partner/GA behavior on the wire.
 *
 * In every role the hook returns `undefined` when no tenant is selected
 * yet (initial app boot) so consumers don't accidentally send
 * `?tenantId=0`.
 *
 * Convention matches `isPartnerView` checks used elsewhere in the
 * dashboard (`PartnerCustomerDropdown`, `ProvisioningDetails`, Resource
 * Manager pages, etc.) for the Partner-role case; the GA-role branch is
 * specific to Tags because that's the only endpoint family where the
 * backend treats "no override" as `findAll` instead of "use JWT".
 */
export function useEffectiveTenantId(): number | undefined {
  return useAppSelector((s) => {
    const { tenantId, partnerId } = s.selection;
    if (!tenantId) return undefined;

    const tenantType = s.userProfile.TenantType;

    // Global Admin: always forward when a selection exists. See the
    // hook docblock — without override the backend findAll'as the whole
    // system, ignoring the SideNav dropdown selection.
    if (tenantType === "GLOBAL_ADMIN") {
      return tenantId;
    }

    // Partner / Customer convention: omit override in Partner view so
    // the backend resolves from JWT + expands as needed.
    if (tenantId === partnerId) return undefined;
    return tenantId;
  });
}
